import { onUnmounted, reactive, type UnwrapNestedRefs } from 'vue';
import type { VideoJsPlayer } from 'video.js';
import type { LeftRight, MoviePlayerCompareModeState } from 'src/models/moviePlayer';

interface Deps {
  getVjsForLr: (lr: LeftRight) => VideoJsPlayer | undefined;
}

interface MoviePlayerCompareModeReversePlayContext {
  playMovieBackward: (lr: LeftRight) => void;
  pauseMovieBackward: (lr: LeftRight) => void;
  resetIntervals: () => void;
}

// useMoviePlayerCompareModePlayback.tsから分割した逆再生
// （setIntervalによるコマ戻し駆動）関連の関数。
// vjsインスタンス自体はuseMoviePlayerCompareModeVjs.tsが保持し、
// 本ファイルはdeps.getVjsForLr経由でのみアクセスする。
export function useMoviePlayerCompareModeReversePlay(
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>,
  deps: Deps,
): UnwrapNestedRefs<MoviePlayerCompareModeReversePlayContext> {
  // 左右それぞれの逆再生（コマ戻し）駆動用のinterval ID
  const intervalRewinds = new Map<LeftRight, number>();

  onUnmounted(() => {
    // 逆再生中に画面が破棄された場合、
    // 破棄済みのvideo.jsインスタンスへの参照が残り続けるのを防ぐ
    resetIntervals();
  });

  const pauseMovieBackward = (lr: LeftRight) => {
    const vjs = deps.getVjsForLr(lr);
    if (!vjs) return;

    vjs.removeClass('vjs-playing');
    vjs.addClass('vjs-paused');
    clearInterval(intervalRewinds.get(lr));
  };
  const playMovieBackward = (lr: LeftRight) => {
    // 動画のフレームレート
    const fps = 15;
    // 逆再生をリセットする
    clearInterval(intervalRewinds.get(lr));
    // 再生速度が速いときに逆再生しようとしても
    // 仕組み上動画のロードが間に合わないので、
    // 再生速度の選択は無視し、常に1.0xの速度で逆再生するようにする。
    const vjs = deps.getVjsForLr(lr);
    if (!vjs) return;

    vjs.addClass('vjs-playing');
    vjs.removeClass('vjs-paused');
    intervalRewinds.set(lr, setInterval(function() {
      if (vjs.currentTime() === 0) {
        pauseMovieBackward(lr);
      } else {
        vjs.currentTime(vjs.currentTime() - (1 / fps));
      }
    }, 1000 / fps));
  };
  // 左右個別の逆再生intervalを両方リセットする。
  // 順再生開始時（playMovie）に、
  // 再生対象でない側も含めて逆再生を止めておきたい
  // 元実装の挙動をそのまま踏襲する。
  const resetIntervals = () => {
    intervalRewinds.forEach(id => clearInterval(id));
    intervalRewinds.clear();
  };

  return reactive({ playMovieBackward, pauseMovieBackward, resetIntervals });
}
